import { useState, useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Users, Shield, ChevronRight, Loader2, AlertCircle, UserPlus, Check, Eye } from "lucide-react";
import { SignedInAsBar } from "@/components/SignedInAsBar";

interface PreviewData {
  groupId: string;
  groupName: string;
  memberCount: number;
  personName: string | null;
  inviteToken: string;
  roundId: string | null;
  matchDate: string | null;
}

interface UnclaimedPlayer {
  personId: string;
  displayName: string;
}

type FlowStep = "preview" | "loading" | "expired" | "select-player" | "confirm" | "claiming" | "done";

export default function InvitePage() {
  const { token } = useParams<{ token: string }>();
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [step, setStep] = useState<FlowStep>("loading");
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<"auth" | "other" | null>(null);
  const [players, setPlayers] = useState<UnclaimedPlayer[]>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<UnclaimedPlayer | null>(null);
  const [isNewPlayer, setIsNewPlayer] = useState(false);

  useEffect(() => {
    if (!token) return;
    fetchPreview();
  }, [token]);

  const fetchPreview = async () => {
    setStep("loading");
    try {
      const res = await fetch(`/api/invites/${token}/preview`);
      if (res.status === 410 || res.status === 404) {
        setStep("expired");
        return;
      }
      if (!res.ok) {
        setError("Something went wrong");
        setStep("expired");
        return;
      }
      const data = await res.json();
      setPreview(data);
      setStep("preview");
    } catch {
      setError("Failed to load invite");
      setStep("expired");
    }
  };

  const handleCTAClick = async () => {
    if (!isAuthenticated) {
      sessionStorage.setItem("invite_return_token", token || "");
      return;
    }
    loadUnclaimedPlayers();
  };

  const loadUnclaimedPlayers = async () => {
    setStep("loading");
    setError(null);
    setErrorKind(null);
    try {
      const res = await fetch(`/api/invites/${token}/unclaimed-players`, {
        credentials: "include",
      });
      if (!res.ok) {
        if (res.status === 410) {
          setStep("expired");
          return;
        }
        if (res.status === 401) {
          // Session is stale/invalid — a plain retry will hit the same 401
          // forever. Surface a re-auth action instead of a generic retry.
          setError("Your session has expired — please sign in again.");
          setErrorKind("auth");
          setStep("preview");
          return;
        }
        throw new Error("Failed to load players");
      }
      const data = await res.json();
      setPlayers(data.players || []);
      setStep("select-player");
    } catch {
      setError("Failed to load player list");
      setErrorKind("other");
      setStep("preview");
    }
  };

  const handleErrorAction = () => {
    if (errorKind === "auth") {
      sessionStorage.setItem("invite_return_token", token || "");
      setLocation(`/auth?mode=signin&returnTo=/invite/${token}`);
      return;
    }
    handleCTAClick();
  };

  useEffect(() => {
    if (isAuthenticated && !authLoading && step === "preview") {
      const returnToken = sessionStorage.getItem("invite_return_token");
      if (returnToken === token) {
        sessionStorage.removeItem("invite_return_token");
        loadUnclaimedPlayers();
      }
    }
  }, [isAuthenticated, authLoading, step]);

  const handleSelectPlayer = (player: UnclaimedPlayer) => {
    setSelectedPlayer(player);
    setIsNewPlayer(false);
    setStep("confirm");
  };

  const handleSelectNewPlayer = () => {
    setSelectedPlayer(null);
    setIsNewPlayer(true);
    setStep("confirm");
  };

  const handleConfirmClaim = async () => {
    setStep("claiming");
    try {
      const body: any = {};
      if (isNewPlayer) {
        body.isNewPlayer = true;
      } else {
        body.personId = selectedPlayer?.personId;
      }
      const res = await apiRequest("POST", `/api/invites/${token}/claim`, body);
      const data = await res.json();
      if (data.success && data.groupId) {
        setStep("done");
        queryClient.invalidateQueries({ queryKey: ["/api/user/groups"] });
        if (data.personId) {
          queryClient.invalidateQueries({ queryKey: ["ledger-rounds", data.personId] });
          queryClient.invalidateQueries({ queryKey: ["ledger-stats", data.personId] });
        }
        setTimeout(() => {
          if (data.roundId && data.roundStatus === "in_progress") {
            setLocation(`/rounds/${data.roundId}/live`);
          } else if (data.roundId) {
            setLocation(`/ledger?tab=history&groupId=${data.groupId}&claimedRoundId=${data.roundId}`);
          } else {
            setLocation(`/ledger?tab=history&groupId=${data.groupId}`);
          }
        }, 1500);
      } else {
        setError(data.error || "Claim failed");
        setStep("select-player");
      }
    } catch (err: any) {
      try {
        const errData = await err?.json?.();
        setError(errData?.error || "Claim failed");
      } catch {
        setError("Claim failed — please try again");
      }
      setStep("select-player");
    }
  };

  if (step === "loading") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="invite-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading invite...</p>
        </div>
      </div>
    );
  }

  if (step === "expired") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6" data-testid="invite-expired">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-gray-400" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Invite Expired</h1>
          <p className="text-sm text-gray-500 mb-6">
            This invite has expired — ask your group organizer for a new link.
          </p>
          <button
            onClick={() => setLocation("/")}
            className="text-sm font-semibold text-emerald-600"
            data-testid="button-go-home"
          >
            Go to DoublePress
          </button>
        </div>
      </div>
    );
  }

  if (step === "done") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="invite-done">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Check className="w-7 h-7 text-emerald-600" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 mb-2">Added to Your Ledger!</h1>
          <p className="text-sm text-gray-500">Taking you there now...</p>
        </div>
      </div>
    );
  }

  if (step === "preview" && preview) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="invite-preview">
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <div className="w-16 h-16 rounded-2xl bg-[#064E3B] flex items-center justify-center mb-6">
            <span className="text-white font-black text-xl">DP</span>
          </div>

          <h1 className="text-2xl font-black text-gray-900 text-center mb-2" data-testid="text-group-name">
            {preview.matchDate
              ? `${new Date(preview.matchDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })} Match`
              : preview.groupName}
          </h1>

          <div className="flex items-center gap-1.5 mb-6">
            <Users className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500" data-testid="text-member-count">
              {preview.memberCount} {preview.memberCount === 1 ? "member" : "members"}
            </span>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-sm w-full mb-8">
            <p className="text-center text-gray-700 text-sm leading-relaxed" data-testid="text-invite-message">
              {preview.personName
                ? `We've been tracking your rounds in ${preview.groupName}. Claim your profile to see your stats, rivalries, and standings.`
                : `You've been invited to join ${preview.groupName} on DoublePress. Claim your profile to see group standings and match history.`}
            </p>
          </div>

          {error && (
            <div
              className="w-full max-w-sm bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-3 flex items-center justify-between gap-3"
              data-testid="text-invite-error"
            >
              <span className="text-left">{error}</span>
              <button
                type="button"
                onClick={handleErrorAction}
                className="shrink-0 font-semibold underline underline-offset-2"
                data-testid={errorKind === "auth" ? "button-invite-error-reauth" : "button-invite-error-retry"}
              >
                {errorKind === "auth" ? "Sign in again" : "Try again"}
              </button>
            </div>
          )}

          {preview.roundId && (
            <button
              onClick={() => setLocation(`/rounds/${preview.roundId}/live`)}
              className="w-full max-w-sm bg-[#064E3B] text-white font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 active:bg-emerald-800 transition-colors mb-3"
              data-testid="button-watch-live"
            >
              <Eye className="w-5 h-5" />
              Watch Live Scorecard
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {isAuthenticated ? (
            <button
              onClick={handleCTAClick}
              className={`w-full max-w-sm font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors ${
                preview.roundId
                  ? 'bg-white border border-gray-200 text-gray-700 active:bg-gray-50'
                  : 'bg-[#064E3B] text-white active:bg-emerald-800'
              }`}
              data-testid="button-claim-cta"
            >
              <Shield className="w-5 h-5" />
              Add this round to my Ledger
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <div className="flex flex-col items-center gap-3 w-full max-w-sm">
              <button
                onClick={() => {
                  sessionStorage.setItem("invite_return_token", token || "");
                  setLocation(`/auth?mode=signup&returnTo=/invite/${token}`);
                }}
                className="w-full font-bold py-4 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors bg-[#064E3B] text-white active:bg-emerald-800"
                data-testid="button-create-account-invite"
              >
                <UserPlus className="w-5 h-5" />
                Create Account & Join Round
                <ChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => {
                  sessionStorage.setItem("invite_return_token", token || "");
                  setLocation(`/auth?mode=signin&returnTo=/invite/${token}`);
                }}
                className="w-full text-sm font-medium py-2 px-6 rounded-xl flex items-center justify-center gap-2 transition-colors bg-white border border-gray-200 text-gray-600 active:bg-gray-50"
                data-testid="button-signin-invite"
              >
                Already have an account? Sign in
              </button>
            </div>
          )}

          <p className="text-xs text-gray-400 mt-4 text-center max-w-xs">
            Every bet tracked. Every hole settled.
          </p>
        </div>
      </div>
    );
  }

  if (step === "select-player") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="invite-select-player">
        <div className="bg-[#064E3B] text-white px-4 py-4">
          <h1 className="font-bold text-lg">Who were you in this match?</h1>
          <p className="text-sm text-emerald-200 mt-1">
            Select your name to add this round to your history and stats.
          </p>
        </div>

        <div className="flex-1 px-4 py-4">
          <SignedInAsBar returnTo={`/invite/${token}`} />
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="text-claim-error">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {players.length > 0 && (
            <div className="mb-6">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">
                Players in This Round
              </p>
              <div className="space-y-2">
                {players.map((p) => (
                  <button
                    key={p.personId}
                    onClick={() => handleSelectPlayer(p)}
                    className="w-full bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between active:bg-gray-50 transition-colors"
                    data-testid={`button-select-player-${p.personId}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-sm font-bold text-emerald-700">
                          {p.displayName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900">{p.displayName}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-300" />
                  </button>
                ))}
              </div>
            </div>
          )}

          <button
            onClick={handleSelectNewPlayer}
            className="w-full bg-gray-100 rounded-xl border border-gray-200 p-4 flex items-center justify-between active:bg-gray-200 transition-colors"
            data-testid="button-new-player"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-gray-500" />
              </div>
              <div className="text-left">
                <span className="font-semibold text-gray-700 block">I'm not listed</span>
                <span className="text-xs text-gray-400">Create a new player profile</span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-gray-300" />
          </button>
        </div>
      </div>
    );
  }

  if (step === "confirm") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6" data-testid="invite-confirm">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 max-w-sm w-full">
          <SignedInAsBar returnTo={`/invite/${token}`} />
          <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6 text-amber-600" />
          </div>

          {isNewPlayer ? (
            <>
              <h2 className="text-lg font-bold text-gray-900 text-center mb-2">Join as New Player?</h2>
              <p className="text-sm text-gray-600 text-center mb-6" data-testid="text-confirm-message">
                You'll join{" "}
                {preview?.matchDate
                  ? `the ${new Date(preview.matchDate).toLocaleDateString("en-US", { month: "short", day: "numeric" })} Match`
                  : "this group"}{" "}
                as a new player. If you have existing match history under a different name, it won't be linked automatically.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-lg font-bold text-gray-900 text-center mb-2">Add This Round to Your Ledger?</h2>
              <p className="text-sm text-gray-600 text-center mb-6" data-testid="text-confirm-message">
                You selected <strong>{selectedPlayer?.displayName}</strong> — is that you? All their match history, Razzy stats, and rivalries will become yours.
              </p>
            </>
          )}

          <div className="space-y-3">
            <button
              onClick={handleConfirmClaim}
              className="w-full bg-[#064E3B] text-white font-bold py-3 px-4 rounded-xl active:bg-emerald-800 transition-colors"
              data-testid="button-confirm-claim"
            >
              {isNewPlayer ? "Join Group" : "Yes, that's me"}
            </button>
            <button
              onClick={() => { setStep("select-player"); setError(null); }}
              className="w-full text-gray-500 font-medium py-3 px-4 rounded-xl active:bg-gray-100 transition-colors"
              data-testid="button-cancel-claim"
            >
              Choose a different player
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === "claiming") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="invite-claiming">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Claiming your profile...</p>
        </div>
      </div>
    );
  }

  return null;
}
